import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageCarousel from '../components/ImageCarousel';
import AttractionModal from '../components/AttractionModal';
import { galleryData } from '../constants/galleryImages';

const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const [modalAttraction, setModalAttraction] = useState<{
    title: string;
    description: string;
    imageSrc: string;
    imageAlt: string;
    linkUrl: string;
  } | null>(null);

  return (
    <div className="bg-surface text-on-surface font-body selection:bg-secondary-container selection:text-on-secondary-container fade-in">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[716px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full relative object-cover bg-primary">
            <ImageCarousel images={galleryData.prizemlje.slice(0, 5)} fullHeight={true} />
          </div>
          <div className="absolute inset-0 bg-primary/20 backdrop-brightness-75 pointer-events-none"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div className="max-w-2xl">
            <span className="font-label text-secondary uppercase tracking-[0.3em] text-sm mb-4 block">{t('aboutUsPage.hero.est')}</span>
            <h1 className="font-headline text-white text-3xl sm:text-5xl md:text-7xl font-light leading-tight">
              {t('aboutUsPage.hero.title')}, <br/> <span className="italic">{t('aboutUsPage.hero.subtitle')}</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Story & Hosts Section */}
      <section className="py-12 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(27,28,25,0.05)]">
                <img alt={t('aboutUsPage.story.hostsAlt')} className="w-full h-full object-cover" src={galleryData['1'][0]}/>
              </div>
              <div className="absolute -bottom-10 -right-10 hidden lg:block w-64 aspect-square bg-surface-container-low p-8 rounded-xl shadow-[0_10px_40px_rgba(27,28,25,0.05)]">
                <p className="font-headline italic text-primary text-xl">{t('aboutUsPage.story.quote')}</p>
                <p className="font-label text-secondary text-xs uppercase tracking-widest mt-4">{t('aboutUsPage.story.quoteAuthor')}</p>
              </div>
            </div>
            <div className="space-y-8">
              <div>
                <h2 className="font-headline text-4xl text-primary mb-6">{t('aboutUsPage.story.title')}</h2>
                <p className="text-lg text-on-surface-variant leading-relaxed">
                  {t('aboutUsPage.story.description')}
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl space-y-4">
                <h3 className="font-label text-secondary uppercase tracking-widest text-xs font-bold">{t('aboutUsPage.story.philosophyLabel')}</h3>
                <p className="text-on-surface-variant italic">
                  {t('aboutUsPage.story.philosophyText')}
                </p>
              </div>
              <div className="flex flex-wrap gap-6 md:gap-12 pt-4">
                <div>
                  <span className="block text-3xl font-headline text-primary">25+</span>
                  <span className="text-xs font-label uppercase tracking-widest text-on-surface-variant">{t('aboutUsPage.stats.yearsLabel')}</span>
                </div>
                <div>
                  <span className="block text-3xl font-headline text-primary">2.5k</span>
                  <span className="text-xs font-label uppercase tracking-widest text-on-surface-variant">{t('aboutUsPage.stats.guestsLabel')}</span>
                </div>
                <div>
                  <span className="block text-3xl font-headline text-primary">4.9/5</span>
                  <span className="text-xs font-label uppercase tracking-widest text-on-surface-variant">{t('aboutUsPage.stats.ratingLabel')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proximity to Sea Section */}
      <section className="py-12 md:py-24 bg-surface-container-low">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-xl">
              <h2 className="font-headline text-4xl text-primary mb-4">{t('aboutUsPage.proximity.title')}</h2>
              <p className="text-on-surface-variant">{t('aboutUsPage.proximity.description')}</p>
            </div>
            <div className="flex items-center gap-2 text-secondary font-label uppercase tracking-widest text-sm border-b border-secondary/30 pb-1">
              <span className="material-symbols-outlined material-symbols-rounded block leading-none">waves</span>
              <span>{t('aboutUsPage.proximity.beachDistance')}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group cursor-pointer">
              <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                <img alt={t('aboutUsPage.proximity.morningSwims.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={galleryData.root[0]}/>
              </div>
              <h4 className="font-headline text-xl text-primary mb-2">{t('aboutUsPage.proximity.morningSwims.title')}</h4>
              <p className="text-sm text-on-surface-variant">{t('aboutUsPage.proximity.morningSwims.desc')}</p>
            </div>
            <div className="group cursor-pointer">
              <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                <img alt={t('aboutUsPage.proximity.promenade.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={galleryData.root[1]}/>
              </div>
              <h4 className="font-headline text-xl text-primary mb-2">{t('aboutUsPage.proximity.promenade.title')}</h4>
              <p className="text-sm text-on-surface-variant">{t('aboutUsPage.proximity.promenade.desc')}</p>
            </div>
            <div className="group cursor-pointer">
              <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                <img alt={t('aboutUsPage.proximity.coves.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={galleryData.root[2]}/>
              </div>
              <h4 className="font-headline text-xl text-primary mb-2">{t('aboutUsPage.proximity.coves.title')}</h4>
              <p className="text-sm text-on-surface-variant">{t('aboutUsPage.proximity.coves.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Attractions Bento Grid */}
      <section className="py-12 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="font-label text-secondary uppercase tracking-widest text-xs mb-4 block">{t('aboutUsPage.attractions.label')}</span>
            <h2 className="font-headline text-4xl text-primary">{t('aboutUsPage.attractions.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 h-auto md:h-[600px]">
            {/* National Parks (Swapped to largest) */}
            <div 
              className="md:col-span-2 md:row-span-2 relative rounded-xl overflow-hidden group cursor-pointer"
              onClick={() => setModalAttraction({
                title: t('aboutUsPage.attractions.parks.title'),
                description: t('aboutUsPage.attractions.parks.desc'),
                imageSrc: galleryData['4'][0],
                imageAlt: t('aboutUsPage.attractions.parks.alt'),
                linkUrl: '#'
              })}
            >
              <img alt={t('aboutUsPage.attractions.parks.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={galleryData['4'][0]}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-8">
                <h4 className="text-white font-headline text-2xl">{t('aboutUsPage.attractions.parks.title')}</h4>
              </div>
            </div>
            
            {/* Vineyards */}
            <div 
              className="md:col-span-2 md:row-span-1 relative rounded-xl overflow-hidden group cursor-pointer"
              onClick={() => setModalAttraction({
                title: t('aboutUsPage.attractions.vineyards.title'),
                description: t('aboutUsPage.attractions.vineyards.desc'),
                imageSrc: galleryData['3'][0],
                imageAlt: t('aboutUsPage.attractions.vineyards.alt'),
                linkUrl: '#'
              })}
            >
              <img alt={t('aboutUsPage.attractions.vineyards.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={galleryData['3'][0]}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-6">
                <h4 className="text-white font-headline text-xl">{t('aboutUsPage.attractions.vineyards.title')}</h4>
              </div>
            </div>

            {/* Biograd na Moru (Old Town, Swapped to smaller) */}
            <div 
              className="md:col-span-1 md:row-span-1 relative rounded-xl overflow-hidden group cursor-pointer"
              onClick={() => setModalAttraction({
                title: t('aboutUsPage.attractions.oldTown.title'),
                description: t('aboutUsPage.attractions.oldTown.desc'),
                imageSrc: galleryData['2'][0],
                imageAlt: t('aboutUsPage.attractions.oldTown.alt'),
                linkUrl: '#'
              })}
            >
              <img alt={t('aboutUsPage.attractions.oldTown.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={galleryData['2'][0]}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-6">
                <h4 className="text-white font-headline text-lg">{t('aboutUsPage.attractions.oldTown.title')}</h4>
              </div>
            </div>
            
            {/* Local Flavors */}
            <div 
              className="md:col-span-1 md:row-span-1 relative rounded-xl overflow-hidden group cursor-pointer"
              onClick={() => setModalAttraction({
                title: t('aboutUsPage.attractions.flavors.title'),
                description: t('aboutUsPage.attractions.flavors.desc'),
                imageSrc: galleryData.prizemlje[1],
                imageAlt: t('aboutUsPage.attractions.flavors.alt'),
                linkUrl: '#'
              })}
            >
              <img alt={t('aboutUsPage.attractions.flavors.alt')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={galleryData.prizemlje[1]}/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-6">
                <h4 className="text-white font-headline text-lg">{t('aboutUsPage.attractions.flavors.title')}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Overlay */}
      <section className="py-12 md:py-24 max-w-7xl mx-auto px-4 md:px-8">
        <div className="bg-gradient-to-br from-[#002045] to-[#1a365d] rounded-3xl p-6 md:p-12 lg:p-20 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 rounded-full -ml-48 -mb-48"></div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="font-headline text-4xl md:text-5xl text-white mb-8">{t('aboutUsPage.cta.title')}</h2>
            <p className="text-[#adc7f7] text-lg mb-10 leading-relaxed">
              {t('aboutUsPage.cta.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Attraction Modal */}
      <AttractionModal
        isOpen={!!modalAttraction}
        onClose={() => setModalAttraction(null)}
        title={modalAttraction?.title || ''}
        description={modalAttraction?.description || ''}
        imageSrc={modalAttraction?.imageSrc || ''}
        imageAlt={modalAttraction?.imageAlt || ''}
        linkUrl={modalAttraction?.linkUrl || '#'}
      />
    </div>
  );
};

export default AboutUs; 